import { inject, Injectable } from '@angular/core';
import { CountryLanguageDetails } from '../../models/utils';
import { CountryLanguageHelper } from '../../utils/country-language/country-language-helper';
import { AppConfigService } from '../utils/api-builder/config/app-config.service';
import { LanguageModalHelper } from './language-modal.helper';
import { LanguageRedirectorService } from './language-redirector.service';

@Injectable({ providedIn: 'root' })
export class LanguageHandlerService {
  appConfig = inject(AppConfigService);
  countryLanguageHelper = inject(CountryLanguageHelper);
  languageRedirector = inject(LanguageRedirectorService);
  modalHelper = inject(LanguageModalHelper);

  get openFlyoutLang() {
    return this.modalHelper.openFlyoutLang;
  }

  handleSupportedLanguage(
    countryCode: string,
    browserLang: string,
    data: CountryLanguageDetails
  ): void {
    this.countryLanguageHelper.setPrefCookie(`${browserLang}-${countryCode}`);
    const needsRedirect = this.shouldRedirect(countryCode, browserLang);

    if (needsRedirect) {
      this.countryLanguageHelper.setShowConfirmationModal(true);
      this.languageRedirector.loadSelectedLanguage(
        countryCode,
        browserLang,
        data.countryLanguages
      );
    } else {
      this.countryLanguageHelper.setShowConfirmationModal(false);
      this.countryLanguageHelper.setPreferredLanguages(
        this.appConfig.getConfig('HEADER')?.['APP_PREF_COOKIE']
      );
    }
  }

  handleUnsupportedLanguage(
    countryCode: string,
    data: CountryLanguageDetails
  ): void {
    const primaryLang =
      this.countryLanguageHelper.getPrimaryLanguage(countryCode);
    if (!primaryLang) return;

    this.countryLanguageHelper.setPrefCookie(
      `${primaryLang.languageCode}-${countryCode}`
    );
    const secondaryLang =
      this.countryLanguageHelper.getSecondaryLanguage(countryCode);
    const domainCorrect =
      this.countryLanguageHelper.isDomainCorrectForPrefCookie();

    this.handleDomainRedirection(
      domainCorrect,
      countryCode,
      primaryLang.languageCode,
      secondaryLang,
      data
    );
  }

  private handleDomainRedirection(
    domainCorrect: boolean,
    countryCode: string,
    primaryLangCode: string,
    secondaryLang: string | null,
    data: CountryLanguageDetails
  ): void {
    if (domainCorrect) {
      secondaryLang &&
        this.modalHelper.openModal(countryCode, primaryLangCode, secondaryLang);
    } else {
      secondaryLang &&
        this.countryLanguageHelper.setShowConfirmationModal(true);
      this.languageRedirector.loadSelectedLanguage(
        countryCode,
        primaryLangCode,
        data.countryLanguages
      );
    }
  }

  showModalIfNeeded(countryCode: string | null): void {
    this.modalHelper.showModalIfNeeded(countryCode);
  }

  private shouldRedirect(countryCode: string, browserLang: string): boolean {
    return (
      countryCode !==
        this.appConfig.getConfig('HEADER')?.['APP_DEFAULT_COUNTRY'] ||
      browserLang !==
        this.appConfig.getConfig('HEADER')?.['APP_DEFAULT_BROWSER_LANG'] ||
      !this.countryLanguageHelper.isDomainCorrectForPrefCookie()
    );
  }
}
